import Order, {ApiOrder, DetailOrderFilters, OrderFilters} from '@/model/Order';
import Suggestion, {ApiSuggestion, DetailSuggestionFilters, SuggestionFilters} from '@/model/Suggestion';
import {CombinedApiResponse, doApiRequest, Method, Module} from '..';
import {parseGenericFilters} from '../meta/filters';
import {ApiSortsDerived} from '../meta/odata';

export enum ResourceBestellung {
  Bestellung = '/Bestellung',
  Suggestion = '/BestellVorschlag'
}

export enum OrderAction {
  DeletePosition = '/position/{posNr}',
  Position = '/position',
}

export interface ApiSuggestionSubmitPosition {
  PosNr: number;
  ArtikelNr: string;
  Menge: number;
  GebindeMenge?: number;
  Niederlassung: number;
  Lagerhalle: number;
  Lagerfach: number;
}

export interface ApiSuggestionSubmitData {
  BuchungNr?: number;
  Bediener: string;
  Datum: string;
  Positionen: ApiSuggestionSubmitPosition[];
}

export async function getOrders(
    filters: OrderFilters, sorts: ApiSortsDerived<ApiOrder>,
    limit: number | null = null, skip: number = 0,
): Promise<CombinedApiResponse<Order[]>> {
  const parsedFilters = parseGenericFilters(filters);
  const response = await doApiRequest<ApiOrder[]>({
    method: Method.Get,
    resource: ResourceBestellung.Bestellung,
    module: Module.Bestellung,
    filters: parsedFilters,
    sorts,
    skip,
    limit,
  });

  return [response.Data.map((o) => Order.fromApi(o)), response.CountAll];
}

export async function getDetailOrder(
    filters: DetailOrderFilters,
): Promise<CombinedApiResponse<Order|null>> {
  if (!filters.Nummer) return [null];
  const response = await doApiRequest({
    module: Module.Bestellung,
    resource: ResourceBestellung.Bestellung,
    method: Method.Get,
    limit: 1,
    filters: parseGenericFilters(filters),
  });

  return response.Data ? [Order.fromApi(response.Data[0])] : [null];
}

export async function getCRMOrders(
    filters: OrderFilters, sorts: ApiSortsDerived<ApiOrder>,
    limit: number | null = null, skip: number = 0,
): Promise<CombinedApiResponse<Order[]>> {
  const parsedFilters = parseGenericFilters(filters);
  const response = await doApiRequest<ApiOrder[]>({
    method: Method.Get,
    resource: ResourceBestellung.Bestellung,
    module: Module.Bestellung,
    filters: parsedFilters,
    select: new Set(['Nummer', 'Datum', 'Uhrzeit', 'Erledigt', 'AnzahlLieferungen']),
    expand: {
      ['Termine']: null,
    },
    sorts,
    skip,
    limit,
  });

  return [response.Data.map((o) => Order.fromApi(o)), response.CountAll];
}

export async function getDetailCRMOrder(
    filters: DetailOrderFilters,
): Promise<CombinedApiResponse<Order|null>> {
  if (!filters.Nummer) return [null];
  const response = await doApiRequest({
    module: Module.Bestellung,
    resource: ResourceBestellung.Bestellung,
    method: Method.Get,
    select: new Set(['Nummer']),
    expand: {
      ['BestellungPosition']: {
        select: new Set(['PosNr']),
        expand: {
          ['Artikel']: {
            select: new Set(['ArtikelNr', 'ArtikelBezeichnung1', 'ArtikelBezeichnung2']),
          },
          ['Einheit']: {
            select: new Set(['Menge', 'Einheit']),
          },
          ['Preise']: {
            select: new Set(['EinheitsPreis', 'NettoBetrag']),
          },
        },
      },
    },
    limit: 1,
    filters: parseGenericFilters(filters),
  });

  return response.Data ? [Order.fromApi(response.Data[0])] : [null];
}
// TODO: select reaktivieren
export async function getSuggestions(
    filters: SuggestionFilters, sorts: ApiSortsDerived<ApiSuggestion>,
    limit: number | null = null, skip: number = 0,
): Promise<CombinedApiResponse<Suggestion[]>> {
  const parsedFilters = parseGenericFilters(filters);
  const response = await doApiRequest<ApiSuggestion[]>({
    method: Method.Get,
    module: Module.Bestellung,
    resource: ResourceBestellung.Suggestion,
    skip,
    limit,
    sorts,
    filters: parsedFilters,
    select: new Set([
      'BuchungNr',
      'ErstelltAm',
      'ErstelltDurch',
      'Erledigt',
    ]),
  });

  return [response.Data.map((i) => Suggestion.fromApi(i)), response.CountAll];
}

// TODO: select + expand reaktivieren
export async function getDetailSuggestion(
    filters: DetailSuggestionFilters,
): Promise<CombinedApiResponse<Suggestion|null>> {
  const parsedFilters = parseGenericFilters(filters);
  const response = await doApiRequest({
    method: Method.Get,
    module: Module.Bestellung,
    resource: ResourceBestellung.Suggestion,
    limit: 1,
    filters: parsedFilters,
    select: new Set([
      'BuchungNr',
      'LieferantNr',
      'ErstelltAm',
      'ErstelltDurch',
      'Erledigt',
      'BestellVorschlagPositionen',
    ]),
    expand: {
      BestellVorschlagPositionen: null,
    },
  });

  return response.Data && response.Data[0] ? [Suggestion.fromApi(response.Data[0])] : [null];
}

export async function submitSuggestion(
    data: ApiSuggestionSubmitData,
) {
  await doApiRequest({
    method: Method.Post,
    module: Module.Bestellung,
    resource: ResourceBestellung.Suggestion,
    data,
  });
}

export async function deleteSuggestion(id: number) {
  await doApiRequest({
    method: Method.Delete,
    module: Module.Bestellung,
    resource: ResourceBestellung.Suggestion,
    id,
  });
}

export async function deleteSuggestionPosition(id: number, posNr: number) {
  await doApiRequest({
    method: Method.Delete,
    module: Module.Bestellung,
    resource: ResourceBestellung.Suggestion,
    id,
    action: OrderAction.DeletePosition,
    actionParameters: {
      posNr,
    },
  });
}

export async function postSuggestionPosition(buchungNr: number, data: { Positionen: ApiSuggestionSubmitPosition[] }) {
  await doApiRequest({
    method: Method.Post,
    module: Module.Bestellung,
    resource: ResourceBestellung.Suggestion,
    id: buchungNr,
    action: OrderAction.Position,
    data,
  });
}

export async function putSuggestionPosition(buchungNr: number, data: { Positionen: ApiSuggestionSubmitPosition[] }) {
  await doApiRequest({
    method: Method.Put,
    module: Module.Bestellung,
    resource: ResourceBestellung.Suggestion,
    id: buchungNr,
    action: OrderAction.Position,
    data,
  });
}
